import React from "react";
import { Navigate } from "react-router-dom";
import { Container } from "@mui/material";
import {
  usePermission,
  useEffectivePermissions,
  checkPermission,
} from "../../hooks/usePermission";
import useAuth from "../../hooks/useAuth";
import { PermissionLoadingState, PermissionRequired } from "../permissions";

interface RouteGuardProps {
  children: React.ReactNode;
  resource: string;
  action: string;
  fallbackPath?: string;
  showError?: boolean;
}

/**
 * Route guard that checks permissions before rendering protected routes
 * Redirects to fallback path if user lacks permission
 *
 * Usage in routes:
 * <RouteGuard resource="invoice" action="read">
 *   <InvoicePage />
 * </RouteGuard>
 */
export const RouteGuard: React.FC<RouteGuardProps> = ({
  children,
  resource,
  action,
  fallbackPath = "/",
  showError = true,
}) => {
  const { currentUser } = useAuth();
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const { hasPermission, loading, error } = usePermission(
    resource,
    action,
    associationId
  );

  // Show loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <PermissionLoadingState variant="page" />
      </Container>
    );
  }

  // Show error if permission check failed
  if (error && showError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <PermissionRequired
          resource={resource}
          action={action}
          message={`Virhe tarkistettaessa käyttöoikeuksia: ${error}`}
          variant="alert"
        />
      </Container>
    );
  }

  // Redirect if no permission
  if (!hasPermission) {
    if (showError) {
      return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <PermissionRequired
            resource={resource}
            action={action}
            variant="card"
            showContactAdmin
          />
        </Container>
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Render children if permission granted
  return <>{children}</>;
};

/**
 * Multiple permission route guard - requires ALL permissions
 */
interface MultiPermissionRouteGuardProps {
  children: React.ReactNode;
  permissions: Array<{ resource: string; action: string }>;
  fallbackPath?: string;
  showError?: boolean;
}

export const MultiPermissionRouteGuard: React.FC<
  MultiPermissionRouteGuardProps
> = ({ children, permissions, fallbackPath = "/", showError = true }) => {
  const { currentUser } = useAuth();
  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  // Fetch all permissions once (instead of calling usePermission in a loop)
  const {
    permissions: userPermissions,
    loading,
    error,
  } = useEffectivePermissions(associationId);

  // Check all required permissions synchronously
  const hasAllPermissions = permissions.every(({ resource, action }) =>
    checkPermission(userPermissions, resource, action)
  );

  // Show loading state
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <PermissionLoadingState variant="page" />
      </Container>
    );
  }

  // Show error if permission check failed
  if (error && showError) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <PermissionRequired
          resource="multiple"
          action="read"
          message={`Virhe tarkistettaessa käyttöoikeuksia: ${error}`}
          variant="alert"
        />
      </Container>
    );
  }

  // Redirect if missing any permission
  if (!hasAllPermissions) {
    if (showError) {
      return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <PermissionRequired
            resource="multiple"
            action="read"
            message="Sinulla ei ole kaikkia tarvittavia käyttöoikeuksia tämän sivun katseluun."
            variant="card"
            showContactAdmin
          />
        </Container>
      );
    }
    return <Navigate to={fallbackPath} replace />;
  }

  // Render children if all permissions granted
  return <>{children}</>;
};
